<template>
  <div class="card">
    <div style="margin-bottom: 16px">
      <a :href="dashboardUrl" class="back-link">← {{ s__('Project|Back to Dashboard') }}</a>
    </div>

    <h2 style="margin: 0 0 20px 0">{{ s__('Project|New Project') }}</h2>

    <div class="wizard-container">
      <div v-if="step === 'type'" class="form-container">
        <div class="step-header">
          <h3 class="step-title">{{ s__('Project|Choose project type') }}</h3>
          <p class="step-indicator">{{ stepIndicator }}</p>
        </div>
        <div class="project-type-options">
          <button
            type="button"
            class="project-type-option"
            :class="{ active: projectType === 'personal' }"
            :disabled="submitting"
            @click="projectType = 'personal'"
          >
            <strong>{{ s__('Project|Private project') }}</strong>
            <span>{{ s__('Project|Only you can access it.') }}</span>
          </button>
          <button
            type="button"
            class="project-type-option"
            :class="{ active: projectType === 'team' }"
            :disabled="submitting"
            @click="projectType = 'team'"
          >
            <strong>{{ s__('Project|Team project') }}</strong>
            <span>{{ s__('Project|Collaborate with other users.') }}</span>
          </button>
        </div>
        <div class="form-actions">
          <a :href="dashboardUrl" class="btn-link-secondary">{{ s__('Project|Cancel') }}</a>
          <button
            type="button"
            class="btn-primary"
            :disabled="submitting || !projectType"
            @click="proceedFromType"
          >
            {{ __('Next') }}
          </button>
        </div>
      </div>

      <div v-else-if="step === 'team-info'" class="form-container">
        <div class="step-header">
          <h3 class="step-title">{{ s__('Project|Create team project') }}</h3>
          <p class="step-indicator">{{ stepIndicator }}</p>
        </div>
        <div class="info-banner">
          <p>
            {{
              s__(
                'Project|Alternative: add the bot to your Telegram group. Members who join via the bot/link will be added to the project automatically.'
              )
            }}
          </p>
        </div>
        <div class="form-actions">
          <button type="button" class="btn-link-secondary" :disabled="submitting" @click="goBack">
            {{ __('Back') }}
          </button>
          <a :href="dashboardUrl" class="btn-link-secondary">{{
            s__('Project|Use Telegram flow instead')
          }}</a>
          <button
            type="button"
            class="btn-primary"
            :disabled="submitting"
            @click="step = 'details'"
          >
            {{ s__('Project|Create via UI') }}
          </button>
        </div>
      </div>

      <div v-else-if="step === 'details'" class="form-container">
        <div class="step-header">
          <h3 class="step-title">{{ s__('Project|Project details') }}</h3>
          <p class="step-indicator">{{ stepIndicator }}</p>
        </div>
        <div class="form-row">
          <label>{{ s__('Project|Title') }} *</label>
          <input
            v-model="title"
            type="text"
            :placeholder="s__('Project|Enter project title')"
            :disabled="submitting"
          />
        </div>

        <div class="form-row">
          <label>{{ s__('Project|Description') }}</label>
          <textarea
            v-model="description"
            :placeholder="s__('Project|Enter project description (optional)')"
            rows="4"
            :disabled="submitting"
          ></textarea>
        </div>

        <div class="form-actions">
          <button type="button" class="btn-link-secondary" :disabled="submitting" @click="goBack">
            {{ __('Back') }}
          </button>
          <a :href="dashboardUrl" class="btn-link-secondary">{{ s__('Project|Cancel') }}</a>
          <button
            type="button"
            class="btn-primary"
            :disabled="submitting || !canProceedFromDetails"
            @click="step = 'workflow'"
          >
            {{ __('Next') }}
          </button>
        </div>
      </div>

      <div v-else class="form-container">
        <div class="step-header">
          <h3 class="step-title">{{ s__('Project|Choose workflow') }}</h3>
          <p class="step-indicator">{{ stepIndicator }}</p>
        </div>
        <div class="form-row">
          <label>{{ s__('Project|Workflow Type') }} *</label>
          <WorkflowTypeSelector
            v-model="workflowType"
            :disabled="submitting"
            :translation-namespace="workflowTranslationNamespace"
          />
        </div>

        <div class="form-actions">
          <button type="button" class="btn-link-secondary" :disabled="submitting" @click="goBack">
            {{ __('Back') }}
          </button>
          <a :href="dashboardUrl" class="btn-link-secondary">{{ s__('Project|Cancel') }}</a>
          <button
            type="button"
            class="btn-primary"
            :disabled="submitting || !canProceedFromDetails"
            @click="createProject"
          >
            {{ submitting ? s__('Project|Creating...') : createButtonLabel }}
          </button>
        </div>
      </div>

      <div v-if="error" class="error" style="margin-top: 12px">{{ error }}</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __ } from '@/locale'
import WorkflowTypeSelector from '../common/WorkflowTypeSelector.vue'

import { removeInitialLoading } from '@/utils/loadingScreen'

const props = defineProps({ chatId: String, fromId: String })

// Remove loading screen immediately since this page has no data to load
removeInitialLoading()

const submitting = ref(false)
const error = ref('')
const step = ref('type')
const projectType = ref(null)
const title = ref('')
const description = ref('')
const workflowType = ref('standard')

const CREATE_PROJECT = gql`
  mutation CreateProject($title: String!, $description: String, $workflowType: String!) {
    createProject(title: $title, description: $description, workflowType: $workflowType) {
      project {
        id
        title
      }
      errors
    }
  }
`

const { mutate: createProjectMutation } = useMutation(CREATE_PROJECT)

const CREATE_PERSONAL_PROJECT = gql`
  mutation CreatePersonalProject($title: String, $description: String, $workflowType: String!) {
    createPersonalProject(title: $title, description: $description, workflowType: $workflowType) {
      project {
        id
        title
      }
      errors
    }
  }
`

const { mutate: createPersonalProjectMutation } = useMutation(CREATE_PERSONAL_PROJECT)

const canProceedFromDetails = computed(() => {
  return title.value.trim().length > 0
})

const stepIndicator = computed(() => {
  if (step.value === 'type') return s__('Project|Step 1 of 3')
  if (step.value === 'details') return s__('Project|Step 2 of 3')
  if (step.value === 'workflow') return s__('Project|Step 3 of 3')
  return s__('Project|Step 1 of 3')
})

const workflowTranslationNamespace = computed(() =>
  projectType.value === 'personal' ? 'PersonalProject' : 'Project'
)

const createButtonLabel = computed(() =>
  projectType.value === 'personal'
    ? s__('PersonalProject|Create Personal Project')
    : s__('Project|Create Project')
)

const dashboardUrl = computed(() => {
  let url = '/dashboard'
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

async function createProject() {
  if (submitting.value || !canProceedFromDetails.value) return

  submitting.value = true
  error.value = ''

  try {
    if (projectType.value === 'personal') {
      const { data } = await createPersonalProjectMutation({
        title: title.value.trim(),
        description: description.value.trim() || null,
        workflowType: workflowType.value,
      })

      if (data?.createPersonalProject?.errors?.length) {
        error.value = data.createPersonalProject.errors.join(', ')
        submitting.value = false
        return
      }

      window.location.href = dashboardUrl.value
    } else {
      const { data } = await createProjectMutation({
        title: title.value.trim(),
        description: description.value.trim() || null,
        workflowType: workflowType.value,
      })

      if (data?.createProject?.errors?.length) {
        error.value = data.createProject.errors.join(', ')
        submitting.value = false
        return
      }

      const projectId = data?.createProject?.project?.id
      if (projectId) {
        let url = `/projects/${projectId}/dashboard`
        const params = []
        if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
        if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
        if (params.length > 0) url += '?' + params.join('&')
        window.location.href = url
      } else {
        window.location.href = dashboardUrl.value
      }
    }
  } catch (e) {
    error.value = e.message || s__('Project|Failed to create project')
    submitting.value = false
  }
}

function proceedFromType() {
  if (!projectType.value) return
  if (projectType.value === 'team') {
    step.value = 'team-info'
    return
  }
  step.value = 'details'
}

function goBack() {
  if (step.value === 'workflow') {
    step.value = 'details'
    return
  }
  if (step.value === 'details') {
    step.value = projectType.value === 'team' ? 'team-info' : 'type'
    return
  }
  if (step.value === 'team-info') {
    step.value = 'type'
  }
}
</script>

<style src="../../styles/forms.css"></style>
<style scoped>
.back-link {
  color: #1d6bf3;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.back-link:hover {
  background-color: #f5f5f5;
  text-decoration: none;
}

.form-container {
  max-width: 600px;
}

.wizard-container {
  max-width: 720px;
}

.step-indicator {
  margin: 0;
  color: #666;
  font-size: 14px;
  margin-left: auto;
  text-align: right;
}

.step-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  width: 100%;
  margin-bottom: 14px;
}

.step-title {
  margin: 0;
  font-size: 18px;
}

.project-type-options {
  display: grid;
  gap: 10px;
  margin-bottom: 16px;
}

.project-type-option {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  padding: 12px;
  display: grid;
  gap: 4px;
  text-align: center;
  justify-items: center;
  color: #333;
}

.project-type-option strong,
.project-type-option span {
  display: block;
  text-align: center;
  width: 100%;
}

.project-type-option:hover:not(:disabled) {
  border-color: #2aabee;
  background: #f8fbff;
}

.project-type-option.active {
  border-color: #2aabee;
  background: #edf7ff;
}

.project-type-option:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.form-row {
  margin-bottom: 20px;
}

.form-row label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #495057;
}

.form-row input[type='text'],
.form-row textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
}

.form-row input[type='text']:focus,
.form-row textarea:focus {
  outline: none;
  border-color: #1d6bf3;
  box-shadow: 0 0 0 3px rgba(29, 107, 243, 0.1);
}

.form-row input[type='text']:disabled,
.form-row textarea:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.form-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
  justify-content: flex-end;
}

.wizard-container .form-actions button,
.wizard-container .form-actions a {
  min-height: 44px;
  padding: 12px 22px;
  font-size: 15px;
}

.info-banner {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
  color: #1e3a8a;
}

.info-banner p {
  margin: 0;
}

.error {
  color: #dc3545;
  font-size: 14px;
  padding: 12px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
}
</style>
